import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

import Button from "../../../../components/buttons/Button";
import TextField from "../../../../components/input/textfield/TextField";
import TextArea from "../../../../components/input/textarea/TextArea";
import Select from "../../../../components/input/select/Select";

import { useCreatorGroups } from "../hooks/useCreatorGroups";

import "./GroupCreatorEditForm.css";

const VISIBILITY_OPTIONS = [
  { label: "Public", value: "public" },
  { label: "Private", value: "private" },
];

const initialFormData = (group) => ({
  groupName: group?.groupName || "",
  groupCode: group?.groupCode || "",
  groupPolicy: group?.groupPolicy || "",
  visibility: String(group?.visibility || "private").toLowerCase(),
});

export default function GroupCreatorEditForm({
  username,
  group,
  onClose,
  onSuccess,
}) {
  const { updateCreatorGroup } = useCreatorGroups(username);

  const [formData, setFormData] = useState(() => initialFormData(group));
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");

  const submittingRef = useRef(false);

  const groupTag = String(group?.groupTag || "");

  const groupName = formData.groupName.trim();
  const groupCode = formData.groupCode.trim();
  const groupPolicy = formData.groupPolicy.trim();
  const visibility = String(formData.visibility || "").toLowerCase();

  const validVisibility = VISIBILITY_OPTIONS.some(
    (option) => option.value === visibility,
  );

  const canSubmit =
    Boolean(username) &&
    Boolean(groupTag) &&
    Boolean(groupName) &&
    Boolean(groupCode) &&
    Boolean(groupPolicy) &&
    validVisibility &&
    !submitting;

  useEffect(() => {
    setFormData(initialFormData(group));
    setFormError("");
  }, [group]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape" && !submittingRef.current) {
        event.preventDefault();
        onClose?.();
      }
    };

    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [onClose]);

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    setFormError("");
  };

  const handleClose = () => {
    if (submittingRef.current) return;

    onClose?.();
  };

  const handleSubmit = async (event) => {
    event?.preventDefault?.();

    if (submittingRef.current) return;

    if (!username || !groupTag) {
      setFormError("Creator or group information is missing.");
      return;
    }

    if (!groupName) {
      setFormError("Group name is required.");
      return;
    }

    if (!groupCode) {
      setFormError("Group code is missing.");
      return;
    }

    if (!groupPolicy) {
      setFormError("Group privacy and rules are required.");
      return;
    }

    if (!validVisibility) {
      setFormError("Please select a valid visibility option.");
      return;
    }

    submittingRef.current = true;
    setSubmitting(true);
    setFormError("");

    try {
      const result = await updateCreatorGroup({
        groupTag,
        groupName,
        groupCode,
        groupPolicy,
        visibility,
      });

      if (!result || result?.success === false) {
        setFormError(result?.message || "Unable to update group.");
        return;
      }

      await onSuccess?.(result);
      onClose?.();
    } catch (error) {
      console.error("Unable to update group:", error);

      setFormError(
        error?.response?.data?.message ||
          error?.message ||
          "Unable to update group.",
      );
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return createPortal(
    <form
      className="creator-edit-group-form"
      onSubmit={handleSubmit}
      aria-label="Edit Group"
      aria-busy={submitting}
    >
      <header className="creator-edit-group-form__header">
        <div className="creator-edit-group-form__header-info">
          <h2>Edit Group</h2>
          <p>Update your group information and settings.</p>
        </div>

        <button
          type="button"
          className="creator-edit-group-form__close"
          onClick={handleClose}
          disabled={submitting}
          aria-label="Close edit group form"
        >
          <X size={20} />
        </button>
      </header>

      <div className="creator-edit-group-form__workspace">
        <main className="creator-edit-group-form__main">
          <div className="creator-edit-group-form__section-header">
            <h3>Group Information</h3>
            <p>Update your group's name, privacy and rules.</p>
          </div>

          <div className="creator-edit-group-form__fields">
            <TextField
              label="Group Name"
              name="groupName"
              value={formData.groupName}
              onChange={handleChange}
              placeholder="e.g. European Tour"
              maxLength={100}
              required
              disabled={submitting}
            />

            <TextArea
              label="Group Privacy & Rules"
              name="groupPolicy"
              value={formData.groupPolicy}
              onChange={handleChange}
              placeholder="Explain the group rules and how members should use its content."
              maxLength={2000}
              maxHeight={180}
              required
              disabled={submitting}
            />
          </div>
        </main>

        <aside className="creator-edit-group-form__sidebar">
          <div className="creator-edit-group-form__section-header">
            <h3>Group Settings</h3>
            <p>Manage your group's access and visibility.</p>
          </div>

          <div className="creator-edit-group-form__fields">
            <TextField
              label="Group Code"
              name="groupCode"
              value={formData.groupCode}
              placeholder="Group access code"
              disabled
            />

            <p className="creator-edit-group-form__help">
              Share this code with people who want to join your group.
            </p>

            <Select
              label="Visibility"
              name="visibility"
              value={visibility}
              onChange={handleChange}
              items={VISIBILITY_OPTIONS}
              required
              disabled={submitting}
            />

            <p className="creator-edit-group-form__help">
              {visibility === "public"
                ? "Your group is publicly discoverable."
                : "Your group is private."}
            </p>
          </div>

          <div className="creator-edit-group-form__summary">
            <h4>Group Summary</h4>

            <div>
              <span>Name</span>
              <strong>{groupName || "—"}</strong>
            </div>

            <div>
              <span>Group Code</span>
              <strong>{groupCode || "—"}</strong>
            </div>

            <div>
              <span>Visibility</span>
              <strong>{visibility || "—"}</strong>
            </div>

            <div>
              <span>Group Rules</span>
              <strong>{groupPolicy ? "Provided" : "Required"}</strong>
            </div>
          </div>
        </aside>
      </div>

      <footer className="creator-edit-group-form__footer">
        <div className="creator-edit-group-form__footer-status">
          {formError ? (
            <span className="creator-edit-group-form__error" role="alert">
              {formError}
            </span>
          ) : submitting ? (
            <span>Saving group changes...</span>
          ) : null}
        </div>

        <div className="creator-edit-group-form__actions">
          <Button
            type="button"
            value="Cancel"
            variant="primary-outlined"
            onClick={handleClose}
            disabled={submitting}
          />

          <Button
            type="submit"
            value={submitting ? "Saving..." : "Save Changes"}
            variant="primary"
            disabled={!canSubmit}
          />
        </div>
      </footer>
    </form>,
    document.body,
  );
}
